import { useEffect, useState } from 'react';
import Card from '@mui/material/Card';
import Chip from '@mui/material/Chip';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';

import SoftBox from 'components/SoftBox';
import SoftButton from 'components/SoftButton';
import SoftTypography from 'components/SoftTypography';

import { useAuth } from 'auth';
import { interpolateTemplate } from 'lib/i18n/dictionary';
import {
  createGalleryRequest,
  fetchMyGalleries,
  galleryErrorMessage,
  galleryStatus,
  markGallerySelectionSeenRequest,
  readCachedMyGalleries,
  summaryCoverUrl,
  visibleSelectionBanners,
  type GallerySummary,
} from 'gallery';

import { formatShootDate, notifyGalleryError, useGalleryText } from './gallery-ui';

const STATUS_COLORS = { ACTIVE: 'success', SELECTION_SENT: 'warning', EXPIRED: 'default' } as const;

const coverPill = {
  bgcolor: 'rgba(0,0,0,.55)',
  color: '#fff',
  borderRadius: 999,
  px: 1,
  py: 0.25,
  fontSize: 12,
  fontWeight: 600,
};

interface GalleryListProps {
  onOpen: (uuid: string, view?: 'selection') => void;
}

export function GalleryList({ onOpen }: GalleryListProps) {
  const { accessToken, activeProfileUuid, isProfilesLoading } = useAuth();
  const { locale, t, plural } = useGalleryText();
  const cached = activeProfileUuid ? readCachedMyGalleries(activeProfileUuid) : undefined;
  const [galleries, setGalleries] = useState<GallerySummary[]>(() => cached ?? []);
  const [isLoading, setIsLoading] = useState(() => cached === undefined);
  const [isCreating, setIsCreating] = useState(false);

  useEffect(() => {
    if (isProfilesLoading || !activeProfileUuid) {
      setIsLoading(isProfilesLoading);
      return;
    }

    let isCancelled = false;
    const fromCache = readCachedMyGalleries(activeProfileUuid);
    if (fromCache) {
      setGalleries(fromCache);
      setIsLoading(false);
    }

    fetchMyGalleries(activeProfileUuid, accessToken ?? undefined)
      .then((result) => {
        if (!isCancelled) {
          setGalleries(result);
        }
      })
      .catch(() => {
        if (!isCancelled) {
          notifyGalleryError(t.title, t.loadFailed);
        }
      })
      .finally(() => {
        if (!isCancelled) {
          setIsLoading(false);
        }
      });

    return () => {
      isCancelled = true;
    };
  }, [accessToken, activeProfileUuid, isProfilesLoading, t.loadFailed, t.title]);

  // Hidden at once; a failed request only brings the banner back with the next load.
  const dismiss = (gallery: GallerySummary) => {
    setGalleries((current) =>
      current.map((entry) =>
        entry.uuid === gallery.uuid ? { ...entry, hasNewSelection: false } : entry
      )
    );
    markGallerySelectionSeenRequest(gallery.uuid, accessToken ?? undefined).catch(() => undefined);
  };

  // The gallery page marks a new selection seen when it opens.
  const open = (gallery: GallerySummary, view?: 'selection') => {
    onOpen(gallery.uuid, view);
  };

  const create = async () => {
    if (!activeProfileUuid || isCreating) {
      return;
    }
    setIsCreating(true);
    try {
      const created = await createGalleryRequest(activeProfileUuid, accessToken ?? undefined);
      onOpen(created.uuid);
    } catch (error) {
      notifyGalleryError(t.title, galleryErrorMessage(error, t.errors));
      setIsCreating(false);
    }
  };

  if (!isProfilesLoading && !activeProfileUuid) {
    return (
      <SoftTypography variant="button" color="text">
        {t.profileMissing}
      </SoftTypography>
    );
  }

  const banners = visibleSelectionBanners(galleries);

  return (
    <SoftBox display="flex" flexDirection="column" gap={2} data-testid="gallery-list">
      {banners.map((gallery) => (
        <Card
          key={gallery.uuid}
          sx={{ p: 2, bgcolor: 'warning.light' }}
          data-testid="gallery-selection-banner"
        >
          <SoftBox display="flex" alignItems="center" gap={1}>
            <SoftTypography variant="button" fontWeight="medium" sx={{ flex: 1 }}>
              {interpolateTemplate(plural(t.banner.text, gallery.selection.count), {
                title: gallery.title.trim() || t.untitled,
              })}
            </SoftTypography>
            <SoftButton
              variant="text"
              color="dark"
              onClick={() => open(gallery, 'selection')}
              data-testid="gallery-selection-banner-view"
            >
              {t.banner.view}
            </SoftButton>
            <IconButton
              size="small"
              aria-label={t.banner.close}
              onClick={() => dismiss(gallery)}
              data-testid="gallery-selection-banner-close"
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </SoftBox>
        </Card>
      ))}
      <SoftTypography variant="h5" fontWeight="medium">
        {t.title}
      </SoftTypography>
      {isLoading ? (
        <SoftTypography variant="button" color="text">
          {t.loading}
        </SoftTypography>
      ) : (
        <SoftBox
          display="grid"
          gap={2}
          sx={{ gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))' }}
        >
          <Card
            component="button"
            type="button"
            onClick={create}
            disabled={isCreating || !activeProfileUuid}
            data-testid="gallery-new"
            sx={{
              minHeight: 260,
              display: 'grid',
              placeItems: 'center',
              cursor: 'pointer',
              border: '2px dashed',
              borderColor: 'grey.300',
              boxShadow: 'none',
              bgcolor: 'transparent',
              font: 'inherit',
            }}
          >
            <SoftTypography variant="h6">{isCreating ? t.creating : t.newGallery}</SoftTypography>
          </Card>
          {galleries.map((gallery) => {
            const cover = summaryCoverUrl(gallery);
            const status = galleryStatus(gallery);
            const date = formatShootDate(gallery.shootDate, locale) ?? t.noDate;
            return (
              <Card
                key={gallery.uuid}
                onClick={() => open(gallery)}
                sx={{ cursor: 'pointer', overflow: 'hidden' }}
                data-testid="gallery-card"
              >
                <SoftBox position="relative">
                  {cover ? (
                    <img
                      src={cover}
                      alt=""
                      style={{ width: '100%', height: 170, objectFit: 'cover', display: 'block' }}
                    />
                  ) : (
                    <SoftBox height={170} bgcolor="grey.200" />
                  )}
                  {/* Guest activity over the cover, as on the app's gallery page. */}
                  <SoftBox
                    display="flex"
                    gap={0.75}
                    position="absolute"
                    right={8}
                    bottom={8}
                    data-testid="gallery-card-activity"
                  >
                    <SoftBox title={t.counters.views} sx={coverPill}>
                      {`👁 ${gallery.counters.views}`}
                    </SoftBox>
                    <SoftBox title={t.counters.selected} sx={coverPill}>
                      {`♥ ${gallery.selection.count}`}
                    </SoftBox>
                  </SoftBox>
                </SoftBox>
                <SoftBox p={2}>
                  <SoftBox
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                    gap={1}
                  >
                    <SoftTypography variant="h6" noWrap>
                      {gallery.title.trim() || t.untitled}
                    </SoftTypography>
                    <Chip
                      size="small"
                      label={t.status[status]}
                      color={STATUS_COLORS[status]}
                      data-testid="gallery-card-status"
                    />
                  </SoftBox>
                  <SoftTypography variant="caption" color="text">
                    {`${date} · ${plural(t.fileCount, gallery.itemCount)}`}
                  </SoftTypography>
                </SoftBox>
              </Card>
            );
          })}
        </SoftBox>
      )}
    </SoftBox>
  );
}
